/**
 * TemplatePicker
 *
 * Dropdown for selecting an event template to populate the event form.
 */

import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { FileText, ChevronDown } from 'lucide-react';
import type { CalendarEventTemplateDetail } from '@/lib/api';

interface TemplatePickerProps {
  templates: CalendarEventTemplateDetail[];
  onSelect: (template: CalendarEventTemplateDetail) => void;
}

export function TemplatePicker({ templates, onSelect }: TemplatePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (templates.length === 0) return null;

  return (
    <div ref={ref} className="relative">
      <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => setOpen(!open)}>
        <FileText className="h-3.5 w-3.5" />
        From template
        <ChevronDown className="h-3 w-3" />
      </Button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 w-56 rounded-md border bg-popover p-1 shadow-md">
          <div className="max-h-48 overflow-y-auto">
            {templates.map((t) => (
              <button
                key={t.id}
                className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-accent transition-colors"
                onClick={() => {
                  onSelect(t);
                  setOpen(false);
                }}
              >
                <div className="font-medium truncate">{t.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {t.title} &middot; {t.durationMinutes}min
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
